import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const ROLE_LABELS = {
  jr_inspector: 'Inspector',
  sr_inspector: 'Reviewer',
  admin: 'Admin',
};

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Employee Code, 2: Registration, 3: Email Verification
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data
  const [empCode, setEmpCode] = useState('');
  const [employeeData, setEmployeeData] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Step 1: Check Employee Code
  const handleCheckEmployee = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await authService.checkEmployee(empCode);
      setEmployeeData(data.employee);
      setFormData(prev => ({
        ...prev,
        email: data.employee.email,
        role: data.employee.role || '',
        emp_code: empCode
      }));
      setSuccess('Employee found! Please complete registration.');
      setStep(2);
    } catch (err) {
      setError(err.error || 'Failed to verify employee code');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Register
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.full_name || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    if (!formData.role) {
      setError('Your role has not been assigned yet. Please contact your administrator.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const registerData = {
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        emp_code: empCode,
        role: formData.role
      };

      const data = await authService.register(registerData);
      setRegisteredEmail(formData.email);
      setSuccess('Registration successful! Please check your email for verification code.');
      setStep(3);
    } catch (err) {
      setError(err.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Verify Email
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);

    try {
      await authService.verifyEmail(registeredEmail, verificationCode);
      setSuccess('Email verified successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      await authService.resendVerification(registeredEmail);
      setSuccess('Verification code sent! Please check your email.');
    } catch (err) {
      setError(err.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-lg">
            <Card.Header className="bg-primary text-white text-center py-3">
              <h3 className="mb-0">
                <i className="fas fa-user-plus me-2"></i>
                Register - Equipment Inspection System
              </h3>
            </Card.Header>
            <Card.Body className="p-4">
              {/* Progress Indicator */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <small className={step >= 1 ? 'text-primary fw-bold' : 'text-muted'}>
                    1. Employee Code
                  </small>
                  <small className={step >= 2 ? 'text-primary fw-bold' : 'text-muted'}>
                    2. Registration
                  </small>
                  <small className={step >= 3 ? 'text-primary fw-bold' : 'text-muted'}>
                    3. Verification
                  </small>
                </div>
                <div className="progress" style={{ height: '5px' }}>
                  <div
                    className="progress-bar bg-primary"
                    style={{ width: `${(step / 3) * 100}%` }}
                  ></div>
                </div>
              </div>

              {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
              {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

              {/* Step 1: Employee Code Verification */}
              {step === 1 && (
                <Form onSubmit={handleCheckEmployee}>
                  <Form.Group className="mb-3">
                    <Form.Label>Employee Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your employee code"
                      value={empCode}
                      onChange={(e) => setEmpCode(e.target.value)}
                      required
                    />
                    <Form.Text className="text-muted">
                      Enter your registered employee code to verify your identity
                    </Form.Text>
                  </Form.Group>

                  <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-search me-2"></i>
                        Verify Employee Code
                      </>
                    )}
                  </Button>
                </Form>
              )}

              {/* Step 2: Registration Form */}
              {step === 2 && (
                <Form onSubmit={handleRegister}>
                  <Alert variant="info">
                    <strong>Employee Found:</strong> {employeeData?.email}
                    <br />
                    <small>Department: {employeeData?.department}</small>
                  </Alert>

                  <Form.Group className="mb-3">
                    <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="full_name"
                      placeholder="Enter your full name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.email}
                      disabled
                      className="bg-light"
                    />
                    <Form.Text className="text-muted">
                      Email is automatically fetched from employee records
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      value={formData.role ? (ROLE_LABELS[formData.role] || formData.role) : 'Not assigned yet'}
                      disabled
                      className="bg-light"
                    />
                    {formData.role ? (
                      <Form.Text className="text-muted">
                        Role is automatically fetched from employee records
                      </Form.Text>
                    ) : (
                      <Form.Text className="text-danger">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        Your role has not been assigned yet. Please contact your administrator.
                      </Form.Text>
                    )}
                    {formData.role === 'admin' && (
                      <Form.Text className="text-warning d-block">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        Admin role requires authorized email
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      placeholder="Create a strong password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength={6}
                    />
                    <Form.Text className="text-muted">
                      Minimum 6 characters
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button variant="primary" type="submit" disabled={loading || !formData.role}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Registering...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-check me-2"></i>
                          Register
                        </>
                      )}
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setStep(1)}>
                      <i className="fas fa-arrow-left me-2"></i>
                      Back
                    </Button>
                  </div>
                </Form>
              )}

              {/* Step 3: Email Verification */}
              {step === 3 && (
                <Form onSubmit={handleVerifyEmail}>
                  <div className="text-center mb-4">
                    <i className="fas fa-envelope-open text-primary" style={{ fontSize: '3rem' }}></i>
                    <h5 className="mt-3">Verify Your Email</h5>
                    <p className="text-muted">
                      We've sent a 6-digit verification code to<br />
                      <strong>{registeredEmail}</strong>
                    </p>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-center w-100">Verification Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="text-center fs-4 letter-spacing-wide"
                      required
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button variant="primary" type="submit" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Verifying...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2"></i>
                          Verify Email
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={handleResendCode}
                      disabled={loading}
                    >
                      <i className="fas fa-redo me-2"></i>
                      Resend Code
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
            <Card.Footer className="text-center py-3">
              <small>
                Already have an account?{' '}
                <a href="/login" className="text-primary fw-bold">
                  Login here
                </a>
              </small>
            </Card.Footer>
          </Card>

          <div className="text-center mt-3">
            <Button variant="outline-secondary" onClick={() => navigate('/')}>
              <i className="fas fa-home me-2"></i>
              Back to Home
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Register;
